import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { analyzePcap, getPacketDetail } from '../../redux/actions/pcapActions';
import classes from './PcapModal.module.css';

const PcapModal = ({ isOpen, onClose, filename }) => {
	const dispatch = useDispatch();

	// State
	const [loading, setLoading] = useState(true);
	const [packets, setPackets] = useState([]);
	const [metadata, setMetadata] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [selectedPacket, setSelectedPacket] = useState(null);
	const [packetDetail, setPacketDetail] = useState(null);
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [activeTab, setActiveTab] = useState('layers'); // 'layers' or 'hexdump'

	const fetchPackets = useCallback(async (page) => {
		setLoading(true);
		try {
			const data = await dispatch(analyzePcap(filename, page, 100));
			if (data && data.status) {
				setPackets(data.packets || []);
				setMetadata(data.metadata || null);
			} else {
				toast.error(data?.msg || 'Failed to analyze pcap file');
			}
		} catch (error) {
			console.error('Error fetching packets:', error);
			toast.error('Error fetching packets');
		} finally {
			setLoading(false);
		}
	}, [dispatch, filename]);

	const fetchPacketDetail = useCallback(async (packetIndex) => {
		setLoadingDetail(true);
		try {
			const data = await dispatch(getPacketDetail(filename, packetIndex));
			if (data && data.status) {
				setPacketDetail(data.packet || null);
			} else {
				toast.error(data?.msg || 'Failed to fetch packet details');
			}
		} catch (error) {
			console.error('Error fetching packet details:', error);
			toast.error('Error fetching packet details');
		} finally {
			setLoadingDetail(false);
		}
	}, [dispatch, filename]);

	// Fetch packets when modal opens or page changes
	useEffect(() => {
		if (isOpen && filename) {
			fetchPackets(currentPage);
		}
	}, [isOpen, filename, currentPage, fetchPackets]);

	// Reset state when modal closes
	useEffect(() => {
		if (!isOpen) {
			setPackets([]);
			setMetadata(null);
			setCurrentPage(1);
			setSelectedPacket(null);
			setPacketDetail(null);
			setActiveTab('layers');
		}
	}, [isOpen]);

	const handlePacketClick = (packet) => {
		setSelectedPacket(packet);
		setPacketDetail(null);
		setActiveTab('layers'); // Reset to layers tab when opening new packet
		fetchPacketDetail(packet.index);
	};

	const handleClosePacketDetail = () => {
		setSelectedPacket(null);
		setPacketDetail(null);
	};

	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= metadata?.totalPages) {
			setCurrentPage(newPage);
			setSelectedPacket(null);
			setPacketDetail(null);
		}
	};

	const getProtocolColor = (protocol) => {
		const protocolColors = {
			TCP: '#3b82f6',
			UDP: '#10b981',
			ICMP: '#f59e0b',
			ARP: '#8b5cf6',
			IP: '#6366f1',
			IPv6: '#ec4899',
			Other: '#6b7280'
		};
		return protocolColors[protocol] || protocolColors.Other;
	};

	const formatTimestamp = (timestamp) => {
		const date = new Date(timestamp * 1000);
		return date.toLocaleString('en-US', {
			hour12: false,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			fractionalSecondDigits: 3
		});
	};

	if (!isOpen) return null;

	return (
		<div className={classes.modalOverlay} onClick={onClose}>
			<div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className={classes.modalHeader}>
					<h2>PCAP Viewer - {filename}</h2>
					<button className={classes.closeButton} onClick={onClose}>
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* Body */}
				<div className={classes.modalBody}>
					{loading ? (
						<div className={classes.loadingContainer}>
							<div className={classes.spinner}></div>
							<p>Loading packets...</p>
						</div>
					) : (
						<>
							{/* Top Panel - Metadata */}
							<div className={classes.metadataPanel}>
								<div className={classes.metadataGrid}>
									<div className={classes.metadataItem}>
										<span className={classes.metadataLabel}>Total Packets:</span>
										<span className={classes.metadataValue}>{metadata?.totalPackets || 0}</span>
									</div>
									<div className={classes.metadataItem}>
										<span className={classes.metadataLabel}>Current Page:</span>
										<span className={classes.metadataValue}>
											{currentPage} of {metadata?.totalPages || 1}
										</span>
									</div>
									<div className={classes.metadataItem}>
										<span className={classes.metadataLabel}>Page Size:</span>
										<span className={classes.metadataValue}>{metadata?.pageSize || 100}</span>
									</div>
								</div>

								{/* Protocol Distribution */}
								{metadata?.protocolDistribution && (
									<div className={classes.protocolDistribution}>
										<span className={classes.metadataLabel}>Protocols:</span>
										<div className={classes.protocolBadges}>
											{Object.entries(metadata.protocolDistribution).map(([protocol, count]) => (
												<span
													key={protocol}
													className={classes.protocolBadge}
													style={{ backgroundColor: getProtocolColor(protocol) }}
												>
													{protocol}: {count}
												</span>
											))}
										</div>
									</div>
								)}
							</div>

							{/* Middle Panel - Packet List */}
							<div className={classes.packetListPanel}>
								<div className={classes.tableContainer}>
									<table className={classes.packetTable}>
										<thead>
											<tr>
												<th>#</th>
												<th>Time</th>
												<th>Source</th>
												<th>Destination</th>
												<th>Protocol</th>
												<th>Length</th>
												<th>Info</th>
											</tr>
										</thead>
										<tbody>
											{packets.map((packet) => (
												<tr
													key={packet.index}
													onClick={() => handlePacketClick(packet)}
													style={{
														borderLeft: `4px solid ${getProtocolColor(packet.protocol)}`
													}}
												>
													<td>{packet.number}</td>
													<td className={classes.timestampCell}>
														{formatTimestamp(packet.timestamp)}
													</td>
													<td>{packet.source}</td>
													<td>{packet.destination}</td>
													<td>
														<span
															className={classes.protocolTag}
															style={{
																backgroundColor: getProtocolColor(packet.protocol)
															}}
														>
															{packet.protocol}
														</span>
													</td>
													<td>{packet.length}</td>
													<td className={classes.infoCell}>{packet.info}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								{/* Pagination */}
								<div className={classes.pagination}>
									<button
										onClick={() => handlePageChange(1)}
										disabled={currentPage === 1}
										className={classes.paginationButton}
									>
										First
									</button>
									<button
										onClick={() => handlePageChange(currentPage - 1)}
										disabled={currentPage === 1}
										className={classes.paginationButton}
									>
										Previous
									</button>
									<span className={classes.pageInfo}>
										Page {currentPage} of {metadata?.totalPages || 1}
									</span>
									<button
										onClick={() => handlePageChange(currentPage + 1)}
										disabled={currentPage === metadata?.totalPages}
										className={classes.paginationButton}
									>
										Next
									</button>
									<button
										onClick={() => handlePageChange(metadata?.totalPages)}
										disabled={currentPage === metadata?.totalPages}
										className={classes.paginationButton}
									>
										Last
									</button>
								</div>
							</div>

						</>
					)}
				</div>

				{/* Packet Detail Modal */}
				{selectedPacket && (
					<div className={classes.detailModalOverlay} onClick={handleClosePacketDetail}>
						<div className={classes.detailModalContent} onClick={(e) => e.stopPropagation()}>
							<div className={classes.detailModalHeader}>
								<h3>Packet #{selectedPacket.number} Details</h3>
								<div className={classes.detailModalHeaderRight}>
									<div className={classes.tabs}>
										<button
											className={activeTab === 'layers' ? classes.activeTab : classes.tab}
											onClick={() => setActiveTab('layers')}
										>
											Protocol Layers
										</button>
										<button
											className={activeTab === 'hexdump' ? classes.activeTab : classes.tab}
											onClick={() => setActiveTab('hexdump')}
										>
											Hex Dump
										</button>
									</div>
									<button className={classes.detailCloseButton} onClick={handleClosePacketDetail}>
										<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
											<path d="M6 18L18 6M6 6l12 12" />
										</svg>
									</button>
								</div>
							</div>

							<div className={classes.detailModalBody}>
								{loadingDetail ? (
									<div className={classes.loadingDetail}>
										<div className={classes.spinner}></div>
										<p>Loading packet details...</p>
									</div>
								) : packetDetail ? (
									<>
										{activeTab === 'layers' && (
											<div className={classes.layersView}>
												{packetDetail.summary && (
													<div className={classes.packetSummary}>
														<strong>Summary:</strong> {packetDetail.summary}
													</div>
												)}
												{packetDetail.layers && packetDetail.layers.map((layer, idx) => (
													<div key={idx} className={classes.layer}>
														<div className={classes.layerHeader}>
															{layer.name}
														</div>
														<div className={classes.layerFields}>
															{Object.entries(layer.fields).map(([fieldName, fieldValue]) => (
																<div key={fieldName} className={classes.field}>
																	<span className={classes.fieldName}>
																		{fieldName}:
																	</span>
																	<span className={classes.fieldValue}>
																		{fieldValue}
																	</span>
																</div>
															))}
														</div>
													</div>
												))}
											</div>
										)}

										{activeTab === 'hexdump' && (
											<div className={classes.hexDumpView}>
												<div className={classes.hexDumpHeader}>
													<button
														className={classes.copyButton}
														onClick={() => {
															navigator.clipboard.writeText(packetDetail.hexRaw || '');
															toast.success('Hex data copied to clipboard');
														}}
													>
														Copy Hex
													</button>
												</div>
												<div className={classes.hexDumpContent}>
													{packetDetail.hexDump && packetDetail.hexDump.map((line, idx) => (
														<div key={idx} className={classes.hexLine}>
															<span className={classes.hexOffset}>
																{line.offset}
															</span>
															<span className={classes.hexBytes}>
																{line.hex}
															</span>
															<span className={classes.hexAscii}>
																{line.ascii}
															</span>
														</div>
													))}
												</div>
											</div>
										)}
									</>
								) : (
									<div className={classes.noDetail}>
										<p>No packet details available</p>
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default PcapModal;
